import { ChannelIconData } from '@/core/services/ChannelsService';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@/core/components/ToastProvider/ToastProvider';
import './ListTable.scss';
import {
  OrderBy,
  ScheduledContent,
  ScheduledPostStatus,
  InstagramMediaType,
  fetchScheduledContent,
} from '@/core/services/SacReplyService';
import { usePages } from '@/core/contexts/PagesContext';
import { useTranslation } from 'react-i18next';
import { Loader } from '@/core/components/Loader/Loader';

interface ListingTableProps {
  heading: string[];
  status: ScheduledPostStatus;
  channel?: ChannelIconData;
  onDelete?: (item: ScheduledContent) => Promise<void>; 
  refreshKey?: number;
}

const BATCH_SIZE = 500;

export const translateMediaType = (mediaType: InstagramMediaType, t: (key: string) => string): string => {
  const translations: Record<InstagramMediaType, string> = {
    [InstagramMediaType.IMAGE]: t('mediaTypes.image'),
    [InstagramMediaType.CAROUSEL]: t('mediaTypes.carousel'),
    [InstagramMediaType.REELS]: t('mediaTypes.reels'),
    [InstagramMediaType.STORIES]: t('mediaTypes.stories'),
  };
  return translations[mediaType] || mediaType;
};

export const translateStatus = (status: string, t: (key: string) => string): string => {
  const translations: Record<string, string> = {
    [ScheduledPostStatus.SCHEDULED]: t('scheduleSection.scheduled'),
    [ScheduledPostStatus.PUBLISHED]: t('scheduleSection.published'),
    [ScheduledPostStatus.FAILED]: t('scheduleSection.failed'),
    [ScheduledPostStatus.CANCELED]: t('scheduleSection.canceled'),
    [ScheduledPostStatus.RETRYING]: t('scheduleSection.retrying'),
  };
  return translations[status] || status;
};

type PreviewItem = {
  url: string;
  poster?: string;
  isVideo: boolean;
};

type MediaPreview = {
  items: PreviewItem[];
  index: number;
  caption: string;
};

const isVideoUrl = (url: string): boolean => /\.(mp4|mov)(?:[?#]|$)/i.test(url);

const getScheduledPreviewItems = (row: ScheduledContent): PreviewItem[] => {
  const carouselItems = row.carousel_items?.map((item) => ({
    url: item.video_url || item.image_url || item.cover_url || '',
    poster: item.cover_url || item.image_url,
    isVideo: Boolean(item.video_url),
  })).filter((item) => Boolean(item.url)) ?? [];
  if (row.media_type === InstagramMediaType.CAROUSEL && carouselItems.length > 0) return carouselItems;

  const imageUrl = row.raw_data?.image_url;
  const coverUrl = row.raw_data?.cover_url;
  const videoUrl = row.raw_data?.video_url;
  const url = videoUrl || imageUrl || coverUrl;
  if (!url) return [];

  return [{
    url,
    poster: coverUrl || (imageUrl && !isVideoUrl(imageUrl) ? imageUrl : undefined),
    isVideo: Boolean(videoUrl) || isVideoUrl(url),
  }];
};

export const ListingTable: React.FC<ListingTableProps> = ({ heading, status, channel, onDelete, refreshKey }: ListingTableProps) => {
  const isScheduledStatus = status === ScheduledPostStatus.SCHEDULED;
  const { showToast } = useToast();
  const [filteredData, setFilteredData] = useState<ScheduledContent[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [mediaPreview, setMediaPreview] = useState<MediaPreview | null>(null);
  const mediaPreviewModalRef = useRef<HTMLBdsModalElement | null>(null);
  const [paginationAnnouncement, setPaginationAnnouncement] = useState('');
  const [paginationFinished, setPaginationFinished] = useState(false);
  const [startedPage, setStartedPage] = useState(1);
  const [dataLoaded, setDataLoaded] = useState(false);
  const { pages, isLoading } = usePages();
  const loadingRef = useRef(false);
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const orderBy: OrderBy = isScheduledStatus ? OrderBy.ASC : OrderBy.DESC;
  const statuses: ScheduledPostStatus[] = useMemo(() => isScheduledStatus
    ? [ScheduledPostStatus.SCHEDULED]
    : [ScheduledPostStatus.PUBLISHED, ScheduledPostStatus.CANCELED, ScheduledPostStatus.FAILED, ScheduledPostStatus.RETRYING], [isScheduledStatus]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case ScheduledPostStatus.PUBLISHED:
        return <bds-icon type={'icon'} theme={'outline'} name={'checkball'} style={{color: 'green'}} />;
      case ScheduledPostStatus.RETRYING:
        return <bds-icon type={'icon'} theme={'outline'} name={'refresh'} style={{color: 'orange'}} />;
      case ScheduledPostStatus.FAILED:
        return <bds-icon type={'icon'} theme={'outline'} name={'error'} style={{color: 'red'}} />;
      case ScheduledPostStatus.CANCELED:
        return <bds-icon type={'icon'} theme={'outline'} name={'close'} style={{color: 'orange'}} />;
      default:
        return <bds-icon type={'icon'} theme={'outline'} name={'error'} style={{color: 'red'}} />;
    }
  };

  const formatDateTime = (date: Date): string => {
    return date.toLocaleString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const itemsPerPage = 5;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredData.length);
  const currentItems = filteredData.slice(startIndex, endIndex);
  
  const localPages = Math.ceil(filteredData.length / itemsPerPage);
  let totalPages = 1;
  if (dataLoaded && filteredData.length > 0) {
    totalPages = paginationFinished ? localPages : localPages + 1;
  }

  const handlePageChange = useCallback(async (newPage: any) => {
    const pageNumber = newPage.detail;
    const maxLocalPages = Math.ceil(filteredData.length / itemsPerPage);
    const needsMoreData = pageNumber > maxLocalPages && !paginationFinished;

    if (needsMoreData) {
      try {
        const pageIds = pages.map(page => page.id);
        const skip = filteredData.length;
        const content = await fetchScheduledContent('facebook_instagram', statuses, pageIds, skip, BATCH_SIZE, orderBy);
        if (content.length < BATCH_SIZE) {
          setPaginationFinished(true);
        }
        setFilteredData(prev => [...prev, ...content]);
        setStartedPage(pageNumber);
      } catch (error) {
        console.error('Error loading more data:', error);
        showToastRef.current({
          variant: 'error',
          title: tRef.current('scheduleSection.errorLoadingMoreData'),
          text: tRef.current('scheduleSection.errorLoadingMoreDataText'),
          icon: 'error',
        });
        return;
      }
    }

    setCurrentPage(pageNumber);
    
    const localPages = Math.ceil(filteredData.length / itemsPerPage);
    let currentTotalPages = 1;
    if (dataLoaded && filteredData.length > 0) {
      currentTotalPages = paginationFinished ? localPages : localPages + 1;
    }
    setPaginationAnnouncement(`Page ${pageNumber} of ${currentTotalPages} loaded`);
  }, [filteredData, paginationFinished, pages, statuses, orderBy, dataLoaded, itemsPerPage]);
  useEffect(() => {
    const modal = mediaPreviewModalRef.current;
    if (!modal) {
      return;
    }

    const closePreview = (event: Event) => {
      const modalStatus = (event as CustomEvent<{ modalStatus: string }>).detail.modalStatus;
      if (modalStatus === 'closed') {
        setMediaPreview(null);
      }
    };

    modal.addEventListener('bdsModalChanged', closePreview);
    return () => modal.removeEventListener('bdsModalChanged', closePreview);
  }, [dataLoaded]);

  const elementId = `pagination-${status}`;
  useEffect(() => {
    const element = document.getElementById(elementId);
    if (element) {
      element.addEventListener('bdsPaginationChange', handlePageChange);
      return () => {
        element.removeEventListener('bdsPaginationChange', handlePageChange);
      };
    }
  }, [elementId, handlePageChange]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    
    if (!pages?.length) {
      setDataLoaded(true);
      return;
    }

    if (loadingRef.current) {
      return;
    }

    // Reset do estado antes de carregar
    setDataLoaded(false);
    setFilteredData([]);
    setCurrentPage(1);
    setPaginationFinished(false);

    const loadData = async () => {
      loadingRef.current = true;

      try {
        const pageIds = pages.map(page => page.id);
        const content = await fetchScheduledContent('facebook_instagram', statuses, pageIds, 0, BATCH_SIZE, orderBy);
        
        setFilteredData(content);
        setCurrentPage(1);
        setPaginationFinished(content.length < BATCH_SIZE);
        setDataLoaded(true);
        
      } catch (error) {
        console.error(`Error loading data for ${status}:`, error);
        showToastRef.current({
          variant: 'error',
          title: tRef.current('scheduleSection.errorLoadingData'),
          text: tRef.current('scheduleSection.errorLoadingDataText'),
          icon: 'error',
        });
      } finally {
        loadingRef.current = false;
      }
    };
    
    loadData();
  }, [pages, isLoading, status, refreshKey, orderBy, showToast, statuses]);

  if (isLoading || !dataLoaded) {
    return <Loader />;
  }

  return (
    <bds-paper>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {paginationAnnouncement}
      </div>
      <bds-grid padding="1" direction="column" gap="2">
        <div className="table-container">
          <div className="sr-only-caption">
            Table showing {status} content items. Currently displaying page {currentPage} of{' '}
            {Math.ceil(filteredData.length / itemsPerPage)} with {currentItems.length} items visible.
          </div>
          <bds-table dense-table={true} aria-label={`${status} content table with ${filteredData.length} items`}>
            <bds-table-header>
              <bds-table-row>
                {heading.map((item, index) => {
                  return <bds-table-th key={`${elementId}-heading-${index}`}>{item}</bds-table-th>;
                })}
              </bds-table-row>
            </bds-table-header>
            <bds-table-body>
              {currentItems.map((row, index) => {
                const pageUsername = pages.find(page => page.id === row.page_id)?.username;
                const caption = row.raw_data?.caption ?? row.caption ?? '';
                const previewItems = getScheduledPreviewItems(row);
                const firstPreview = previewItems[0];
                const thumbnailUrl = firstPreview?.poster || firstPreview?.url;
                const isVideoThumbnail = firstPreview?.isVideo === true && !firstPreview.poster;
                return (
                  <bds-table-row
                    key={`${elementId}-item-${row._id}-${index}`}
                    aria-label={`Content item ${index + 1}: ${caption}, status ${status}`}
                  >
                    <bds-table-cell>
                      <img src={channel?.icons.default} alt={t('common.socialMediaIcon')} width="25px" height="25px" loading="lazy" decoding="async" fetchPriority="low" />
                    </bds-table-cell>
                    <bds-table-cell>
                      {thumbnailUrl ? (
                        <button
                          className="scheduled-content-thumbnail-button"
                          type="button"
                          aria-label={`Open media preview for ${caption}`}
                          onClick={() => setMediaPreview({ items: previewItems, index: 0, caption })}
                        >
                          {isVideoThumbnail ? (
                            <video
                              className="scheduled-content-thumbnail"
                              src={thumbnailUrl}
                              autoPlay
                              muted
                              loop
                              playsInline
                              preload="metadata"
                            />
                          ) : (
                            <img
                              className="scheduled-content-thumbnail"
                              src={thumbnailUrl}
                              alt=""
                              loading="lazy"
                              decoding="async"
                              fetchPriority="low"
                            />
                          )}
                        </button>
                      ) : null}
                    </bds-table-cell>
                    <bds-table-cell>
                      <bds-typo variant="fs-14">{caption}</bds-typo>
                    </bds-table-cell>
                    <bds-table-cell>
                      <bds-typo variant="fs-14">{pageUsername}</bds-typo>
                    </bds-table-cell>
                    <bds-table-cell>
                      <bds-typo variant="fs-14">{translateMediaType(row.media_type, t)}</bds-typo>
                    </bds-table-cell>
                    {isScheduledStatus ? null : (
                      <bds-table-cell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {getStatusIcon(row.status)}
                          <bds-typo variant="fs-14">{translateStatus(row.status, t)}</bds-typo>
                        </div>
                      </bds-table-cell>
                    )}
                    <bds-table-cell>
                      <bds-typo variant="fs-14">{formatDateTime(row.scheduled_to)}</bds-typo>
                    </bds-table-cell>
                    <bds-table-cell>
                      <bds-button-icon
                        aria-label={`delete ${status} content scheduled for ${formatDateTime(
                          row.scheduled_to,
                        )} with caption ${caption}`}
                        size="short"
                        variant="secondary--white"
                        icon="trash"
                        disabled={!onDelete}
                        onClick={() => onDelete?.(row)}
                        iconTheme="outline"
                      ></bds-button-icon>
                    </bds-table-cell>
                  </bds-table-row>
                );
              })}
            </bds-table-body>
          </bds-table>
        </div>
        <bds-grid xxs="12" justify-content="flex-end">
          {dataLoaded && filteredData.length > 0 && (
            <bds-pagination
              id={elementId}
              startedPage={startedPage}
              pages={totalPages}
              aria-label={`${status} content table pagination`}
            ></bds-pagination>
          )}
        </bds-grid>
      </bds-grid>
      <bds-modal
        ref={mediaPreviewModalRef}
        open={mediaPreview !== null}
        size="dynamic"
        closeButton={true}
        outzoneClose={true}
        enterClose={true}
      >
        {mediaPreview && (
          <div className="scheduled-content-preview-content">
            {mediaPreview.items[mediaPreview.index]?.isVideo ? (
              <video
                className="scheduled-content-preview-media"
                src={mediaPreview.items[mediaPreview.index].url}
                poster={mediaPreview.items[mediaPreview.index].poster}
                controls
                autoPlay
                muted
                playsInline
                preload="metadata"
              />
            ) : (
              <img
                className="scheduled-content-preview-media"
                src={mediaPreview.items[mediaPreview.index]?.url}
                alt={mediaPreview.caption}
                decoding="async"
                fetchPriority="high"
              />
            )}
            {mediaPreview.items.length > 1 && (
              <div className="scheduled-content-preview-navigation">
                <button
                  type="button"
                  disabled={mediaPreview.index === 0}
                  onClick={() => setMediaPreview((current) => current ? { ...current, index: current.index - 1 } : current)}
                >{t('carousel.previousItem')}</button>
                <bds-typo tag="span" variant="fs-14">
                  {t('carousel.previewPosition', { current: mediaPreview.index + 1, total: mediaPreview.items.length })}
                </bds-typo>
                <button
                  type="button"
                  disabled={mediaPreview.index === mediaPreview.items.length - 1}
                  onClick={() => setMediaPreview((current) => current ? { ...current, index: current.index + 1 } : current)}
                >{t('carousel.nextItem')}</button>
              </div>
            )}
          </div>
        )}
      </bds-modal>
    </bds-paper>
  );
};
